import axios from 'axios';
import { toast } from 'react-hot-toast';
import { getAccessToken, isEvictedState } from './tokenManager';
import { refreshTokenFromCookie } from './getAuthToken';
import { isQrSendRefusal, qrRefusalToastId } from "./qrSendRefusal";

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Inbox open-thread calls fail quietly (invalid URL id, deleted contact): avoid duplicate
 * react-hot-toast "Server error" spam; ChatBox snackbar is suppressed when no chat is selected.
 */
function suppressGlobal500ToastForInboxOpenChat(originalRequest) {
  if (typeof window === 'undefined' || !String(window.location.pathname || '').includes('/inbox')) {
    return false;
  }
  const url = String(originalRequest?.url || '');
  return (
    url.includes('/api/messages/getbycontact/') ||
    url.includes('/api/contacts/getbyid/') ||
    url.includes('/contacts/getbyid/')
  );
}

// Global network error handler with enhanced security features
export const setupNetworkErrorHandler = () => {
  // Request interceptor - adds auth token from memory (not localStorage)
  axios.interceptors.request.use(
    async (config) => {
      // Check if we're offline before making the request
      if (!navigator.onLine) {
        console.log('🚫 Offline detected, cancelling request to:', config.url);
        return Promise.reject(new Error('Network offline'));
      }

      // Add authentication token from memory (not localStorage)
      // Only add token if not already present in headers
      if (!config.headers.Authorization) {
        const token = getAccessToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handles errors, rate limiting, and auto-refresh
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle authentication errors (401 status) - Auto-refresh token
      // Skip refresh if this IS the refresh endpoint (to prevent infinite loops)
      const isRefreshEndpoint = originalRequest.url?.includes('/api/auth/refresh');
      
      if (error.response?.status === 401 && error.response.data?.expired === true && !isRefreshEndpoint && !isEvictedState()) {
        // Token expired - try to refresh
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                if (token) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  return axios(originalRequest);
                } else {
                  return Promise.reject(new Error('Token refresh failed'));
                }
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          isRefreshing = true;

          try {
            // Delegate to the single shared refresh implementation (getAuthToken.js).
            // It already de-dupes concurrent callers with an in-flight promise, so
            // this joins the same request instead of firing a competing one against
            // the single-use refresh-token cookie (which would make one of them lose
            // and look like a dead session).
            const newAccessToken = await refreshTokenFromCookie();

            if (!newAccessToken) {
              throw new Error('Token refresh failed');
            }

            // Process queued requests
            processQueue(null, newAccessToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed - clear token and redirect to login
            processQueue(refreshError, null);

            const { clearAccessToken } = require('./tokenManager');
            clearAccessToken();

            // Clear user data from localStorage
            localStorage.removeItem('user');

            // Only redirect if not already on login page and not a refresh endpoint error
            if (window.location.pathname !== '/' && !isRefreshEndpoint) {
              window.location.href = '/';
            }

            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }
      
      // Handle refresh endpoint failures specifically
      if (error.response?.status === 401 && isRefreshEndpoint) {
        // Refresh token is missing/expired - clear everything and redirect
        const { clearAccessToken } = require('./tokenManager');
        clearAccessToken();
        localStorage.removeItem('user');
        
        // Don't redirect if already on login page
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
        
        return Promise.reject(error);
      }

      // Handle rate limiting errors (429 status)
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'] || 
                          error.response.data?.retryAfter || 
                          '15 minutes';
        const message = error.response.data?.message || 
                       'Too many requests. Please try again later.';
        
        toast.error(`${message} (Retry after: ${retryAfter})`, {
          id: 'rate-limit',
          duration: 5000,
        });
        
        // Return a structured error for components to handle
        return Promise.reject({
          ...error,
          isRateLimitError: true,
          retryAfter,
          message
        });
      }

      // Handle subscription access errors (403)
      if (error.response?.status === 403) {
        const code = error.response.data?.code;
        const SUBSCRIPTION_CODES = ['PAYMENT_FAILED', 'SUBSCRIPTION_INACTIVE', 'PAYMENT_PENDING', 'TRIAL_EXPIRED', 'LIMIT_EXCEEDED', 'FEATURE_NOT_IN_PLAN'];
        if (SUBSCRIPTION_CODES.includes(code)) {
          const message = error.response.data?.message || 'Your subscription is not active. Please update your plan.';
          toast.error(message, { id: 'subscription-blocked', duration: 5000 });
          return Promise.reject(error);
        }
        // Non-subscription 403 (role permission denied) — pass through unchanged
      }

      // Handle validation errors (400 status)
      if (error.response?.status === 400 && error.response.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessage = validationErrors
          .map(err => `${err.field}: ${err.message}`)
          .join(', ');
        
        toast.error(`Validation Error: ${errorMessage}`, {
          duration: 4000,
        });
        
        return Promise.reject({
          ...error,
          isValidationError: true,
          validationErrors
        });
      }

      // Handle other authentication errors (401 status) - not expired, just invalid
      // Skip if this is the refresh endpoint (already handled above)
      // Skip if a token refresh is currently in flight (isRefreshing) to avoid race-condition logout
      if (error.response?.status === 401 && !error.response.data?.expired && !isRefreshEndpoint && !isRefreshing) {
        const { clearAccessToken } = require('./tokenManager');
        clearAccessToken();

        // An evicted tab (signed in elsewhere) is expected to keep failing 401s
        // like this quietly in the background — it already showed its toast and
        // deliberately isn't trying to re-authenticate. Don't hard-redirect it;
        // just let this request fail. See tokenManager.js's isEvicted flag.
        if (!isEvictedState()) {
          // Invalid token (not expired) - clear and redirect
          localStorage.removeItem('user');
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
      }

      // Handle network errors (no response)
      if (!error.response && (
        error.code === 'ECONNABORTED' || 
        error.code === 'ETIMEDOUT' || 
        error.code === 'ENOTFOUND' ||
        error.code === 'ECONNREFUSED' ||
        error.message.includes('timeout') ||
        error.message.includes('Network Error') ||
        error.message.includes('ERR_NETWORK') ||
        error.message.includes('Network offline')
      )) {
        console.log('🚫 Network error detected:', error.message);
        // Don't show toast for network errors - let components handle it
        return Promise.reject({
          ...error,
          isNetworkError: true,
          message: 'NETWORK_ERROR'
        });
      }

      // A refusal the QR send path made on purpose (resting after a burst, daily cap reached, number not connected ...)
      // is NOT a server outage even when it travels as HTTP 503: keep its real message and code (the send flow reads
      // the code) and show that message once, instead of the generic "Server is temporarily unavailable" below.
      if (error.response?.status >= 500 && isQrSendRefusal(error.response.data)) {
        if (error.response.data.message) {
          toast.error(error.response.data.message, { id: qrRefusalToastId(error.response.data.code), duration: 6000 });
        }
        return Promise.reject(error);
      }

      // Handle server errors (500+)
      if (error.response?.status >= 500) {
        // Normalize the response body: nginx 502/503 and other gateway errors return raw HTML.
        // Replace it with a clean JSON-like object so no slice or component ever receives HTML as data.
        const status = error.response.status;
        const isGatewayError = status === 502 || status === 503 || status === 504;
        if (isGatewayError || typeof error.response.data === 'string') {
          error.response.data = {
            message: isGatewayError
              ? 'Server is temporarily unavailable. Please try again in a moment.'
              : 'Server error. Please try again later.',
            status,
          };
        }

        if (!suppressGlobal500ToastForInboxOpenChat(originalRequest)) {
          toast.error(error.response.data.message || 'Server error. Please try again later.', {
            id: 'server-error',
            duration: 4000,
          });
        }
      }

      // For other errors, return the error as-is for components to handle
      return Promise.reject(error);
    }
  );
};

// Function to check if an error is a network error
export const isNetworkError = (error) => {
  return error.message === 'NETWORK_ERROR' || 
         (!error.response && (
           error.code === 'ECONNABORTED' || 
           error.code === 'ETIMEDOUT' || 
           error.code === 'ENOTFOUND' ||
           error.code === 'ECONNREFUSED' ||
           error.message.includes('timeout') ||
           error.message.includes('Network Error') ||
           error.message.includes('ERR_NETWORK')
         ));
};


